// frontend/src/admin/pages/AdminKnowledgePage.jsx
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  deleteKnowledgeChunk,
  deleteKnowledgeSource,
  ingestKnowledgeFile,
  ingestKnowledgeUrl,
  listKnowledgeChunks,
  searchKnowledge,
} from "../api";

const DOC_TYPES = [
  { value: "faq", label: "FAQ" },
  { value: "api_doc", label: "API doc" },
  { value: "pricing", label: "Pricing" },
  { value: "support_case", label: "Support case" },
  { value: "other", label: "Other" },
];

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 max-w-3xl">
      <h2 className="text-sm font-semibold text-slate-700 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function DocTypeSelect({ id, value, onChange, label }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </span>
      <select
        id={id}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-slate-300 rounded-md px-3 py-2 text-sm w-full"
      >
        {DOC_TYPES.map((d) => (
          <option key={d.value} value={d.value}>
            {d.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * AdminKnowledgePage — operator console for the RAG knowledge base (P17-G12).
 *
 * Four sections: ingest a file, ingest a URL, browse/delete chunks, and test
 * retrieval. Ingestion is asynchronous on the backend (a Celery task), so the
 * ingest sections surface the queued task id + chunk count rather than waiting.
 */
function AdminKnowledgePage() {
  // ── Section 1: ingest file ──
  const [file, setFile] = useState(null);
  const [fileDocType, setFileDocType] = useState("faq");
  const [ingestingFile, setIngestingFile] = useState(false);
  const [fileResult, setFileResult] = useState(null);

  // ── Section 2: ingest URL ──
  const [url, setUrl] = useState("");
  const [urlDocType, setUrlDocType] = useState("faq");
  const [ingestingUrl, setIngestingUrl] = useState(false);
  const [urlResult, setUrlResult] = useState(null);

  // ── Section 3: browser ──
  const [chunks, setChunks] = useState([]);
  const [filterSource, setFilterSource] = useState("");
  const [filterDocType, setFilterDocType] = useState("");
  const [loadingChunks, setLoadingChunks] = useState(false);

  // ── Section 4: test retrieval ──
  const [query, setQuery] = useState("");
  const [topK, setTopK] = useState(5);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const loadChunks = async () => {
    setLoadingChunks(true);
    try {
      const params = { per_page: 100 };
      if (filterSource.trim()) params.source_doc = filterSource.trim();
      if (filterDocType) params.doc_type = filterDocType;
      const data = await listKnowledgeChunks(params);
      setChunks(data.items ?? []);
    } catch {
      toast.error("Failed to load knowledge chunks.");
    } finally {
      setLoadingChunks(false);
    }
  };

  useEffect(() => {
    loadChunks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIngestFile = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a file first.");
      return;
    }
    setIngestingFile(true);
    setFileResult(null);
    try {
      const res = await ingestKnowledgeFile(file, fileDocType);
      setFileResult(res);
      toast.success(`Queued ${res.chunks_queued} chunk(s) from ${res.source_doc}.`);
    } catch {
      toast.error("Ingestion failed.");
    } finally {
      setIngestingFile(false);
    }
  };

  const onIngestUrl = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("Enter a URL first.");
      return;
    }
    setIngestingUrl(true);
    setUrlResult(null);
    try {
      const res = await ingestKnowledgeUrl(url.trim(), urlDocType);
      setUrlResult(res);
      toast.success(`Queued ${res.chunks_queued} chunk(s) from ${res.source_doc}.`);
    } catch {
      toast.error("Failed to fetch or ingest the URL.");
    } finally {
      setIngestingUrl(false);
    }
  };

  const onDeleteChunk = async (id) => {
    if (!window.confirm("Delete this chunk?")) return;
    try {
      await deleteKnowledgeChunk(id);
      setChunks((prev) => prev.filter((c) => c.id !== id));
      toast.success("Chunk deleted.");
    } catch {
      toast.error("Failed to delete chunk.");
    }
  };

  const onDeleteSource = async (sourceDoc) => {
    if (!window.confirm(`Delete ALL chunks for "${sourceDoc}"?`)) return;
    try {
      const res = await deleteKnowledgeSource(sourceDoc);
      toast.success(`Deleted ${res.deleted} chunk(s).`);
      loadChunks();
    } catch {
      toast.error("Failed to delete source.");
    }
  };

  const onSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error("Enter a query first.");
      return;
    }
    setSearching(true);
    setResults(null);
    try {
      const data = await searchKnowledge({ query: query.trim(), top_k: topK });
      setResults(data.results ?? []);
    } catch {
      toast.error("Search failed.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 mb-5">
        Knowledge Base
      </h1>

      {/* ── Section 1: Ingest Document ── */}
      <Section title="Ingest Document">
        <form onSubmit={onIngestFile} className="space-y-4">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              File (.md or .txt)
            </span>
            <input
              type="file"
              accept=".md,.txt"
              aria-label="Knowledge document file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block text-sm"
            />
          </label>
          <DocTypeSelect
            id="file-doc-type"
            label="Document type"
            value={fileDocType}
            onChange={setFileDocType}
          />
          <button
            type="submit"
            disabled={ingestingFile}
            className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
          >
            {ingestingFile ? "Ingesting…" : "Ingest"}
          </button>
          {fileResult && (
            <p className="text-sm text-emerald-700">
              Queued {fileResult.chunks_queued} chunk(s) — task{" "}
              <code className="text-xs">{fileResult.task_id}</code>
            </p>
          )}
        </form>
      </Section>

      {/* ── Section 2: Ingest URL ── */}
      <Section title="Ingest URL">
        <form onSubmit={onIngestUrl} className="space-y-4">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              URL
            </span>
            <input
              type="url"
              aria-label="Knowledge URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/help/article"
              className="border border-slate-300 rounded-md px-3 py-2 text-sm w-full"
            />
          </label>
          <DocTypeSelect
            id="url-doc-type"
            label="URL document type"
            value={urlDocType}
            onChange={setUrlDocType}
          />
          <button
            type="submit"
            disabled={ingestingUrl}
            className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
          >
            {ingestingUrl ? "Fetching…" : "Fetch & Ingest"}
          </button>
          {urlResult && (
            <p className="text-sm text-emerald-700">
              Queued {urlResult.chunks_queued} chunk(s) — task{" "}
              <code className="text-xs">{urlResult.task_id}</code>
            </p>
          )}
        </form>
      </Section>

      {/* ── Section 3: Knowledge Base Browser ── */}
      <Section title="Knowledge Base Browser">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Filter by source doc
            </span>
            <input
              type="text"
              aria-label="Filter by source doc"
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Filter by doc type
            </span>
            <select
              aria-label="Filter by doc type"
              value={filterDocType}
              onChange={(e) => setFilterDocType(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={loadChunks}
            className="bg-slate-700 text-white text-sm px-4 py-2 rounded-md"
          >
            Apply Filters
          </button>
        </div>

        {loadingChunks ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : chunks.length === 0 ? (
          <p className="text-sm text-slate-500">No chunks found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2 pr-2">Source Doc</th>
                <th className="py-2 pr-2">Doc Type</th>
                <th className="py-2 pr-2">Content Preview</th>
                <th className="py-2 pr-2">Chunk</th>
                <th className="py-2 pr-2">Date</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {chunks.map((c) => (
                <tr key={c.id} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-2 font-medium text-slate-700">
                    {c.source_doc}
                  </td>
                  <td className="py-2 pr-2 text-slate-500">
                    {c.metadata?.doc_type ?? "—"}
                  </td>
                  <td className="py-2 pr-2 text-slate-600">{c.content}</td>
                  <td className="py-2 pr-2 text-slate-500">{c.chunk_index}</td>
                  <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">
                    {c.created_at
                      ? new Date(c.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="py-2 space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onDeleteChunk(c.id)}
                      aria-label={`Delete chunk ${c.id}`}
                      className="text-rose-600 hover:underline"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteSource(c.source_doc)}
                      className="text-rose-500 hover:underline text-xs"
                    >
                      Delete all for source
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* ── Section 4: Test Retrieval ── */}
      <Section title="Test Retrieval">
        <form onSubmit={onSearch} className="space-y-4">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Query
            </span>
            <input
              type="text"
              aria-label="Retrieval query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Top K: {topK}
            </span>
            <input
              type="range"
              min="1"
              max="20"
              aria-label="Top K"
              value={topK}
              onChange={(e) => setTopK(Number(e.target.value))}
              className="w-64"
            />
          </label>
          <button
            type="submit"
            disabled={searching}
            className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
          >
            {searching ? "Searching…" : "Search"}
          </button>
        </form>

        {results && (
          <div className="mt-4 space-y-3">
            {results.length === 0 ? (
              <p className="text-sm text-slate-500">No matches above threshold.</p>
            ) : (
              results.map((r, i) => (
                <div
                  key={`${r.source_doc}-${i}`}
                  className="border border-slate-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">
                      {r.source_doc}
                    </span>
                    <span className="text-xs text-slate-500">
                      {(r.similarity_score * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div
                    className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2"
                    role="progressbar"
                    aria-label={`Similarity ${r.source_doc}`}
                    aria-valuenow={Math.round(r.similarity_score * 100)}
                  >
                    <div
                      className="h-full bg-indigo-500"
                      style={{ width: `${Math.min(100, r.similarity_score * 100)}%` }}
                    />
                  </div>
                  <p className="text-sm text-slate-600">{r.content}</p>
                </div>
              ))
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

export default AdminKnowledgePage;
